import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, Trophy, Target, Zap, Shield,
  ArrowRight, CheckCircle, BarChart3, DollarSign,
  Calendar, Percent, Award, ChevronRight, Crown, AlertTriangle, Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Backtest verileri (6 ay, TOP 10 coin) - v7.0
// R to $ conversion: $10,000 account, 2% risk = $200 per R
// Aylık backtest performans verileri (%)
const MONTHLY_BACKTEST = {
  july: 18.2,      // Temmuz 2025
  august: 24.6,    // Ağustos 2025
  september: -8.4, // Eylül 2025
  october: 31.2,   // Ekim 2025
  november: 42.8,  // Kasım 2025
  december: 15.4,  // Aralık 2025
  // Ocak 2026 backtest'te yok, canlı veriden geliyor
};

// Backtest toplam: Temmuz-Aralık arası
const BACKTEST_TOTAL_MONTHLY = Object.values(MONTHLY_BACKTEST).reduce((a, b) => a + b, 0); // ~123.8%

const BACKTEST_DATA = {
  period: "6 Ay",
  startDate: "Temmuz 2025",
  endDate: "Ocak 2026",
  totalTrades: 613,
  totalPnlR: 129.1,
  totalPnlDollar: 25820, // 129.1 * 200
  winRate: 54.0,
  profitFactor: 1.46,
  maxDrawdown: 9.5,
  maxDrawdownDollar: 1900, // 9.5 * 200
  avgPnlPerTrade: 0.211,
  avgPnlPerTradeDollar: 42, // 0.211 * 200
  signalsPerDay: 3.41,
  accountStart: 10000,
  accountEnd: 35820, // 10000 + 25820
  coins: [
    { symbol: "ETH", pnl: 20.5, pnlDollar: 4100, winRate: 54.1, trades: 85, color: "#627EEA" },
    { symbol: "APT", pnl: 20.2, pnlDollar: 4040, winRate: 59.6, trades: 52, color: "#00D4AA" },
    { symbol: "AAVE", pnl: 13.8, pnlDollar: 2760, winRate: 52.7, trades: 55, color: "#B6509E" },
    { symbol: "ADA", pnl: 13.5, pnlDollar: 2700, winRate: 52.6, trades: 57, color: "#0033AD" },
    { symbol: "NEAR", pnl: 13.5, pnlDollar: 2700, winRate: 54.9, trades: 71, color: "#00C08B" },
    { symbol: "LINK", pnl: 11.3, pnlDollar: 2260, winRate: 58.5, trades: 65, color: "#2A5ADA" },
    { symbol: "SOL", pnl: 10.5, pnlDollar: 2100, winRate: 50.6, trades: 83, color: "#9945FF" },
    { symbol: "DOGE", pnl: 10.4, pnlDollar: 2080, winRate: 51.0, trades: 49, color: "#C2A633" },
    { symbol: "FET", pnl: 8.5, pnlDollar: 1700, winRate: 54.5, trades: 44, color: "#1D2951" },
    { symbol: "UNI", pnl: 6.9, pnlDollar: 1380, winRate: 51.9, trades: 52, color: "#FF007A" }
  ],
  // Equity curve simulation (normalized)
  equityCurve: [
    0, 2.1, 5.3, 3.2, 8.4, 12.1, 9.8, 15.2, 18.7, 14.3,
    20.5, 25.1, 22.8, 28.9, 35.2, 31.4, 38.7, 42.1, 45.8, 41.2,
    48.9, 55.3, 52.1, 58.7, 65.2, 62.8, 70.1, 75.8, 72.3, 80.5,
    85.2, 82.1, 88.9, 95.3, 91.7, 98.2, 105.8, 102.1, 110.5, 118.2,
    115.3, 122.8, 129.1
  ]
};

// Mini Equity Curve Component with Interactive Tooltip and BTC Benchmark
const EquityCurveChart = ({ data, height = 120, showBenchmark = false }) => {
  const [hoverInfo, setHoverInfo] = useState(null);
  const containerRef = React.useRef(null);
  
  // BTC benchmark data (6 ay için yaklaşık BTC performansı: Tem-Ara 2025)
  const btcBenchmark = [
    0, 5, 12, 8, 18, 25, 15, 28, 22, 35, 30, 42, 38, 50, 45, 55, 48, 60, 52, 65,
    58, 72, 68, 78, 70, 82, 75, 88, 80, 92, 85, 95, 90, 98, 92, 100, 95, 105, 98, 110, 102, 108, 105
  ];
  
  // Normalize benchmark to match data length
  const normalizedBtc = data.map((_, i) => {
    const btcIndex = Math.floor((i / data.length) * btcBenchmark.length);
    return btcBenchmark[Math.min(btcIndex, btcBenchmark.length - 1)];
  });
  
  const allValues = showBenchmark ? [...data, ...normalizedBtc] : data;
  const maxVal = Math.max(...allValues);
  const minVal = Math.min(...allValues);
  const range = maxVal - minVal || 1;
  
  // Data is now in % format directly
  const formatPercent = (value) => value.toFixed(0);
  // Example dollar calculation on $10,000 account with given % return
  const percentToDollar = (percent) => Math.round(10000 * percent / 100);
  
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((val - minVal) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  // BTC benchmark line
  const btcPoints = normalizedBtc.map((val, i) => {
    const x = (i / (normalizedBtc.length - 1)) * 100;
    const y = 100 - ((val - minVal) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  // Gradient area
  const areaPoints = `0,100 ${points} 100,100`;
  
  // Calculate time label from index
  const getTimeLabel = (index, totalPoints) => {
    // Estimate based on data density
    if (index === 0) return "Başlangıç";
    if (index >= totalPoints - 1) return "Güncel";
    const progress = Math.round((index / totalPoints) * 100);
    return `%${progress} ilerleme`;
  };
  
  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    
    // Calculate which data point we're closest to
    const percentage = x / width;
    const index = Math.min(Math.max(Math.round(percentage * (data.length - 1)), 0), data.length - 1);
    const value = data[index];
    const btcValue = normalizedBtc[index];
    
    // Calculate pixel position for the dot
    const dotX = (index / (data.length - 1)) * width;
    const dotY = height - ((value - minVal) / range) * height;
    
    setHoverInfo({
      value,
      btcValue,
      index,
      x: dotX,
      y: dotY,
      timeLabel: getTimeLabel(index, data.length),
      percentGain: formatPercent(value),
      dollarGain: percentToDollar(value)
    });
  };
  
  const handleMouseLeave = () => {
    setHoverInfo(null);
  };
  
  // Final values for the label
  const finalPercent = formatPercent(data[data.length - 1]);
  const finalBtc = formatPercent(normalizedBtc[normalizedBtc.length - 1]);
  
  return (
    <div 
      ref={containerRef}
      className="relative w-full cursor-crosshair" 
      style={{ height }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id="equityGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {/* Area fill */}
        <polygon
          points={areaPoints}
          fill="url(#equityGradient)"
        />
        {/* BTC Benchmark Line (dashed) */}
        {showBenchmark && (
          <polyline
            points={btcPoints}
            fill="none"
            stroke="#f59e0b"
            strokeWidth="0.6"
            strokeDasharray="2,2"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.7"
          />
        )}
        {/* Main Line - System Performance */}
        <polyline
          points={points}
          fill="none"
          stroke="#10b981"
          strokeWidth="0.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      
      {/* Legend for benchmark */}
      {showBenchmark && (
        <div className="absolute top-2 left-2 flex items-center gap-3 text-[10px]">
          <div className="flex items-center gap-1">
            <div className="w-3 h-0.5 bg-emerald-500 rounded"></div>
            <span className="text-zinc-400">Sistem</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-0.5 bg-amber-500 rounded" style={{backgroundImage: 'repeating-linear-gradient(90deg, #f59e0b 0px, #f59e0b 3px, transparent 3px, transparent 6px)'}}></div>
            <span className="text-zinc-400">BTC Hold</span>
          </div>
        </div>
      )}
      
      {/* Hover indicator line and dot */}
      {hoverInfo && (
        <>
          {/* Vertical line */}
          <div 
            className="absolute top-0 w-px bg-emerald-500/50 pointer-events-none"
            style={{ 
              left: hoverInfo.x, 
              height: '100%'
            }}
          />
          {/* Dot on the line */}
          <div 
            className="absolute w-3 h-3 bg-emerald-500 rounded-full border-2 border-white shadow-lg pointer-events-none transform -translate-x-1/2 -translate-y-1/2"
            style={{ 
              left: hoverInfo.x, 
              top: hoverInfo.y
            }}
          />
          {/* Enhanced Tooltip - SPK Compliant */}
          <div 
            className="absolute bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 shadow-2xl pointer-events-none transform -translate-x-1/2 z-10 min-w-[180px]"
            style={{ 
              left: Math.min(Math.max(hoverInfo.x, 80), containerRef.current?.clientWidth - 80 || 0), 
              top: Math.max(hoverInfo.y - 95, 5)
            }}
          >
            {/* Main: Percent */}
            <div className="text-emerald-400 font-bold text-lg">
              {hoverInfo.value >= 0 ? '+' : ''}%{hoverInfo.percentGain}
            </div>
            <div className="text-zinc-400 text-xs mb-2">Sistem Performansı</div>
            
            {/* BTC Comparison */}
            {showBenchmark && (
              <div className="flex items-center gap-1.5 text-sm border-t border-zinc-800 pt-2 mb-2">
                <span className="text-zinc-500">BTC Hold:</span>
                <span className="text-amber-400 font-semibold">+%{formatPercent(hoverInfo.btcValue)}</span>
              </div>
            )}
            
            {/* Dollar example */}
            <div className="flex items-center gap-1.5 text-sm border-t border-zinc-800 pt-2">
              <span className="text-zinc-500">$10K ile:</span>
              <span className="text-white font-semibold">
                ${(10000 + hoverInfo.dollarGain).toLocaleString()}
              </span>
            </div>
            
            {/* Time label */}
            <div className="text-zinc-600 text-xs mt-2">
              {hoverInfo.timeLabel}
            </div>
          </div>
        </>
      )}
      
      {/* Labels */}
      <div className="absolute -bottom-5 left-0 text-[10px] text-zinc-400">Başlangıç</div>
      <div className="absolute top-2 right-2 text-base font-bold text-emerald-400 bg-zinc-900/70 px-2 py-1 rounded">
        +%{finalPercent}
      </div>
      {showBenchmark && (
        <div className="absolute top-10 right-2 text-xs text-amber-400 bg-zinc-900/70 px-2 py-1 rounded">
          BTC: +%{finalBtc}
        </div>
      )}
    </div>
  );
};

// Drawdown Curve Component
const DrawdownChart = ({ height = 100 }) => {
  // Drawdown data (negatif değerler - düşüş yüzdeleri)
  const drawdownData = [
    0, -2.1, -1.5, -4.8, -3.2, -6.5, -4.1, -2.8, -5.2, -3.8,
    -7.2, -5.5, -3.2, -8.4, -6.1, -4.5, -2.8, -5.8, -9.5, -7.2,
    -5.1, -3.4, -6.2, -4.8, -2.5, -5.5, -3.8, -1.9, -4.2, -2.8,
    -5.8, -4.1, -2.2, -3.5, -1.8, -4.5, -2.9, -1.2, -3.2, -1.5, -2.8, -1.1, -0.5
  ];
  
  const maxDrawdown = Math.min(...drawdownData);
  const minVal = maxDrawdown;
  const maxVal = 0;
  const range = maxVal - minVal || 1;
  
  const points = drawdownData.map((val, i) => {
    const x = (i / (drawdownData.length - 1)) * 100;
    const y = 100 - ((val - minVal) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  const areaPoints = `0,0 ${points} 100,0`;
  
  return (
    <div className="relative w-full" style={{ height }}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id="drawdownGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        {/* Zero line */}
        <line x1="0" y1="0" x2="100" y2="0" stroke="#3f3f46" strokeWidth="0.3" />
        {/* Area fill */}
        <polygon
          points={areaPoints}
          fill="url(#drawdownGradient)"
        />
        {/* Line */}
        <polyline
          points={points}
          fill="none"
          stroke="#ef4444"
          strokeWidth="0.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      
      {/* Labels */}
      <div className="absolute top-1 left-2 text-[10px] text-zinc-500">0%</div>
      <div className="absolute bottom-1 left-2 text-[10px] text-red-400 font-semibold">
        Maks: {maxDrawdown.toFixed(1)}%
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, color = "emerald", delay = 0 }) => {
  const colorClasses = {
    emerald: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 text-emerald-400",
    blue: "from-blue-500/20 to-blue-500/5 border-blue-500/30 text-blue-400",
    amber: "from-amber-500/20 to-amber-500/5 border-amber-500/30 text-amber-400",
    purple: "from-purple-500/20 to-purple-500/5 border-purple-500/30 text-purple-400"
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
    >
      <Card className={`bg-gradient-to-br ${colorClasses[color]} border backdrop-blur-sm`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon className="w-4 h-4 opacity-70" />
            <span className="text-xs text-zinc-400">{label}</span>
          </div>
          <div className="text-2xl font-bold text-white">{value}</div>
          {subValue && <div className="text-xs text-zinc-500 mt-1">{subValue}</div>}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Mini Sparkline Component
const MiniSparkline = ({ data, color = "#10b981" }) => {
  const maxVal = Math.max(...data);
  const minVal = Math.min(...data);
  const range = maxVal - minVal || 1;
  
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((val - minVal) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-16 h-8">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Coin Performance Row with Sparkline - Top 5
const CoinRow = ({ coin, index, leverageMultiplier = 1 }) => {
  const basePnl = coin.pnlDollar / 100; // Base percentage
  const displayPnl = basePnl * leverageMultiplier; // Apply leverage
  const isPositive = displayPnl > 0;
  const isNegative = displayPnl < 0;
  
  // Gerçekçi sparkline: PnL'e göre trend oluştur
  const generateSparkline = (pnl) => {
    const points = 10;
    const data = [0];
    
    if (pnl === 0) {
      // Sıfır: Düz çizgi (hafif dalgalanma)
      for (let i = 1; i < points; i++) {
        data.push(Math.random() * 4 - 2); // -2 ile +2 arası
      }
    } else if (pnl > 0) {
      // Pozitif: Yukarı trend (düzensiz)
      let current = 0;
      for (let i = 1; i < points; i++) {
        const progress = i / points;
        const target = pnl * progress;
        const noise = (Math.random() - 0.5) * Math.abs(pnl) * 0.3;
        current = target + noise;
        data.push(current);
      }
      data.push(pnl); // Son nokta gerçek PnL
    } else {
      // Negatif: Aşağı trend (düzensiz)
      let current = 0;
      for (let i = 1; i < points; i++) {
        const progress = i / points;
        const target = pnl * progress;
        const noise = (Math.random() - 0.5) * Math.abs(pnl) * 0.3;
        current = target + noise;
        data.push(current);
      }
      data.push(pnl); // Son nokta gerçek PnL
    }
    
    return data;
  };
  
  const data = generateSparkline(displayPnl);
  
  // Medal for top 3
  const medals = ['🥇', '🥈', '🥉'];
  const medal = index < 3 ? medals[index] : null;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.08 }}
      className="flex items-center justify-between py-3 border-b border-zinc-800/50 last:border-0"
    >
      <div className="flex items-center gap-3">
        {medal ? (
          <span className="text-xl w-8 text-center">{medal}</span>
        ) : (
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
            style={{ backgroundColor: coin.color }}
          >
            {coin.symbol.slice(0, 2)}
          </div>
        )}
        <div>
          <div className="font-medium text-white">{coin.symbol}</div>
          <div className="text-xs text-zinc-500">{coin.trades} sinyal • %{coin.winRate.toFixed(0)} başarılı</div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <MiniSparkline 
          data={data} 
          color={isNegative ? "#ef4444" : isPositive ? "#10b981" : "#6b7280"} 
        />
        <div className={`text-sm font-semibold min-w-[50px] text-right ${
          isNegative ? 'text-red-400' : isPositive ? 'text-emerald-400' : 'text-zinc-400'
        }`}>
          {isPositive ? '+' : ''}{Math.round(displayPnl)}%
        </div>
      </div>
    </motion.div>
  );
};

// Risk & Getiri Simülatörü - Professional Financial Calculator
const RiskReturnSimulator = ({ productionStats, productionLoading, allTimeStats }) => {
  const [accountSize, setAccountSize] = useState(10000);
  const [leverage, setLeverage] = useState('spot'); // spot, 3x, 5x, 10x
  const [showInfo, setShowInfo] = useState(null);
  
  // Kaldıraç seçenekleri - Gerçek kripto borsası kaldıraçları
  const leverageConfig = {
    spot: { multiplier: 1, label: 'Spot', color: 'blue' },
    '3x': { multiplier: 3, label: '3x', color: 'emerald' },
    '5x': { multiplier: 5, label: '5x', color: 'amber' },
    '10x': { multiplier: 10, label: '10x', color: 'red' }
  };
  
  const currentLeverage = leverageConfig[leverage];
  
  // GERÇEK PERFORMANS VERİSİ: Production API'den gelen TÜM ZAMANLAR değeri
  // allTimeStats.signals içindeki pnl_percent toplamı + backtest
  // SADECE 2026 ve sonrası sinyalleri al (backtest ile çakışmayı önle)
  const backtestTotalPercent = BACKTEST_TOTAL_MONTHLY; // ~123.8%
  const liveTotalPercent = allTimeStats?.signals 
    ? allTimeStats.signals
        .filter(s => s.created_at && new Date(s.created_at).getFullYear() >= 2026)
        .reduce((sum, s) => sum + (s.pnl_percent || 0), 0)
    : 0;
  const allTimePnlPercent = backtestTotalPercent + liveTotalPercent; // TÜM ZAMANLAR
  
  // Kaldıraçlı hesaplama
  const leveragedPnlPercent = allTimePnlPercent * currentLeverage.multiplier;
  const totalProfit = accountSize * (leveragedPnlPercent / 100);
  const finalPortfolio = accountSize + totalProfit;
  const percentGain = leveragedPnlPercent;
  
  // Toplam ay sayısı (6 backtest + canlı aylar - sadece 2026+)
  const liveMonthCount = allTimeStats?.signals 
    ? new Set(
        allTimeStats.signals
          .filter(s => s.created_at && s.created_at.startsWith('2026'))
          .map(s => s.created_at.substring(0, 7))
      ).size 
    : 0;
  const totalMonths = 6 + liveMonthCount;
  const monthlyAvgPercent = allTimePnlPercent / totalMonths; // Kaldıraçsız aylık ortalama
  
  // Annualized projection (12 aya göre) - kaldıraçlı
  const annualizedPercent = (allTimePnlPercent / totalMonths) * 12 * currentLeverage.multiplier;
  const annualizedProfit = accountSize * (annualizedPercent / 100);
  const annualizedFinal = accountSize + annualizedProfit;
  
  // Max drawdown kaldıraçla artar
  const leveragedDrawdown = BACKTEST_DATA.maxDrawdown * currentLeverage.multiplier;
  
  const presets = [1000, 5000, 10000, 25000, 50000];
  
  // Mini growth chart data points
  const growthPoints = [0, 15, 28, 22, 45, 62, 55, 78, 95, 88, 115, 130, allTimePnlPercent];
  
  return (
    <Card className="bg-zinc-900/50 border border-zinc-800">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="w-5 h-5 text-emerald-400" />
          <h3 className="font-semibold text-white">Risk & Getiri Simülatörü</h3>
        </div>
        <p className="text-xs text-zinc-500 mb-5">Tüm zamanlar performansına dayalı</p>
        
        {/* Capital Input Section */}
        <div className="mb-5">
          <label className="text-xs text-zinc-400 mb-2 block">Başlangıç Sermayesi</label>
          
          {/* Slider */}
          <div className="relative mb-3">
            <input
              type="range"
              min="1000"
              max="50000"
              step="1000"
              value={accountSize}
              onChange={(e) => setAccountSize(Number(e.target.value))}
              className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-emerald-500"
              style={{
                background: `linear-gradient(to right, #10b981 0%, #10b981 ${((accountSize - 1000) / 49000) * 100}%, #27272a ${((accountSize - 1000) / 49000) * 100}%, #27272a 100%)`
              }}
            />
            <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 bg-emerald-500 text-black text-xs font-bold px-2 py-0.5 rounded -mt-6">
              ${accountSize.toLocaleString()}
            </div>
          </div>
          
          {/* Preset Segmented Control */}
          <div className="flex rounded-lg bg-zinc-800/50 p-1">
            {presets.map(size => (
              <button
                key={size}
                onClick={() => setAccountSize(size)}
                className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
                  accountSize === size
                    ? 'bg-zinc-700 text-white'
                    : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                {size >= 1000 ? `${size/1000}K` : size}
              </button>
            ))}
          </div>
        </div>
        
        {/* Leverage Selector */}
        <div className="mb-5">
          <label className="text-xs text-zinc-400 mb-2 block">Kaldıraç Oranı</label>
          <div className="flex rounded-lg bg-zinc-800/50 p-1">
            {Object.entries(leverageConfig).map(([key, config]) => (
              <button
                key={key}
                onClick={() => setLeverage(key)}
                className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                  leverage === key
                    ? config.color === 'blue' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : config.color === 'emerald' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : config.color === 'amber' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                    : 'bg-red-500/20 text-red-400 border border-red-500/30'
                    : 'text-zinc-500 hover:text-zinc-300 border border-transparent'
                }`}
              >
                {config.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-zinc-600 mt-1.5 text-center">
            {leverage === 'spot' ? 'Kaldıraçsız işlem, en düşük risk' : 
             leverage === '3x' ? 'Getiri ve risk 3 katına çıkar' :
             leverage === '5x' ? 'Getiri ve risk 5 katına çıkar' :
             '⚠️ Yüksek risk! Getiri ve kayıp 10 katına çıkar'}
          </p>
        </div>
        
        {/* Divider */}
        <div className="border-t border-zinc-800 my-5"></div>
        
        {/* HERO: Main Result */}
        <div className="text-center mb-5">
          <div className="text-xs text-zinc-500 mb-2">{totalMonths} Ay Sonunda Portföy Değeri</div>
          <div className="text-4xl font-bold text-white mb-1">
            {productionLoading ? (
              <span className="animate-pulse bg-zinc-700 rounded w-32 h-10 inline-block" />
            ) : (
              `$${finalPortfolio.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
            )}
          </div>
          <div className={`inline-flex items-center gap-1 font-semibold ${percentGain >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            <TrendingUp className="w-4 h-4" />
            {percentGain >= 0 ? '+' : ''}%{percentGain.toFixed(1)}
          </div>
          
          {/* Start → End */}
          <div className="flex items-center justify-center gap-3 mt-3 text-sm">
            <span className="text-zinc-500">Başlangıç: <span className="text-zinc-300">${accountSize.toLocaleString()}</span></span>
            <span className="text-zinc-600">→</span>
            <span className="text-zinc-500">Net: <span className={totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}>{totalProfit >= 0 ? '+' : ''}${totalProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></span>
          </div>
        </div>
        
        {/* Mini Growth Chart */}
        <div className="bg-zinc-800/30 rounded-lg p-3 mb-5">
          <div className="h-12 relative">
            <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-full">
              <defs>
                <linearGradient id="simGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#10b981" stopOpacity="0.05" />
                </linearGradient>
              </defs>
              {/* Area */}
              <polygon
                points={`0,40 ${growthPoints.map((v, i) => `${(i / (growthPoints.length - 1)) * 100},${40 - (v / Math.max(...growthPoints)) * 35}`).join(' ')} 100,40`}
                fill="url(#simGradient)"
              />
              {/* Line */}
              <polyline
                points={growthPoints.map((v, i) => `${(i / (growthPoints.length - 1)) * 100},${40 - (v / Math.max(...growthPoints)) * 35}`).join(' ')}
                fill="none"
                stroke="#10b981"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="absolute bottom-0 left-0 text-[9px] text-zinc-600">Başlangıç</div>
            <div className="absolute bottom-0 right-0 text-[9px] text-zinc-600">{totalMonths}. Ay</div>
          </div>
        </div>
        
        {/* Secondary Metrics */}
        <div className="grid grid-cols-3 gap-2 mb-5">
          <div className="bg-zinc-800/50 rounded-lg p-2.5 text-center">
            <div className="text-[10px] text-zinc-500 mb-1">Aylık Ort.</div>
            <div className="text-sm font-bold text-emerald-400">+%{(monthlyAvgPercent * currentLeverage.multiplier).toFixed(1)}</div>
            <div className="text-[9px] text-zinc-600">(tarihsel)</div>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-2.5 text-center">
            <div className="text-[10px] text-zinc-500 mb-1">Maks Düşüş</div>
            <div className={`text-sm font-bold ${leveragedDrawdown > 50 ? 'text-red-400' : 'text-amber-400'}`}>
              -%{Math.min(leveragedDrawdown, 100).toFixed(1)}
            </div>
            <div className="text-[9px] text-zinc-600">(en kötü)</div>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-2.5 text-center">
            <div className="text-[10px] text-zinc-500 mb-1">Win Rate</div>
            <div className="text-sm font-bold text-white">%{BACKTEST_DATA.winRate}</div>
            <div className="text-[9px] text-zinc-600">(sabit)</div>
          </div>
        </div>
        
        {/* Projection (12 months) */}
        <div 
          className="bg-zinc-800/30 rounded-lg p-3 mb-4 relative group cursor-help"
          onMouseEnter={() => setShowInfo('projection')}
          onMouseLeave={() => setShowInfo(null)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">Projeksiyon (12 Ay)</span>
              <div className="w-4 h-4 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] text-zinc-400">
                i
              </div>
            </div>
            <span className="text-sm font-semibold text-zinc-300">
              ${annualizedFinal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </div>
          <div className="text-[10px] text-zinc-600 mt-1">
            Aynı performans sürerse (garanti değil)
          </div>
          
          {/* Info Tooltip */}
          {showInfo === 'projection' && (
            <div className="absolute bottom-full left-0 right-0 mb-2 p-3 bg-zinc-800 border border-zinc-700 rounded-lg text-xs z-10">
              <div className="text-zinc-300 font-medium mb-1">Matematiksel Yıllıklandırma</div>
              <div className="text-zinc-500">
                {totalMonths} aylık sonuç × {(12/totalMonths).toFixed(1)} = 12 aylık tahmin. 
                <span className="text-amber-400 block mt-1">Bu sadece matematiksel bir hesaplamadır, garanti değildir.</span>
              </div>
            </div>
          )}
        </div>
        
        {/* Leverage Warning for high leverage */}
        {(leverage === '5x' || leverage === '10x') && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 mb-4 text-center">
            <span className="text-[10px] text-red-400">
              ⚠️ Yüksek kaldıraç = Yüksek likidasyon riski. Stop-loss kullanın!
            </span>
          </div>
        )}
        
        {/* Compact Disclaimer */}
        <div className="text-[10px] text-zinc-600 leading-relaxed border-t border-zinc-800 pt-3">
          <span className="text-zinc-500">⚠️</span> Tüm zamanlar performansına dayalı simülasyon. Kaldıraçlı işlemler yüksek risk taşır. Yatırım tavsiyesi değildir.
        </div>
      </CardContent>
    </Card>
  );
};

const PerformanceDashboard = () => {
  const { user } = useAuth();
  const [liveStats, setLiveStats] = useState(null);
  const [productionStats, setProductionStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [productionLoading, setProductionLoading] = useState(true);  // Track production loading separately
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [equityCurveData, setEquityCurveData] = useState(null);
  const [curveLoading, setCurveLoading] = useState(true);
  const [leverageMultiplier, setLeverageMultiplier] = useState(1); // 1x default (Spot)
  const [showLeverageWarning, setShowLeverageWarning] = useState(false);
  
  // Leverage seçenekleri
  const leverageOptions = [
    { value: 1, label: 'Spot', sublabel: 'Gerçek' },
    { value: 3, label: '3x', sublabel: 'Simülasyon' },
    { value: 5, label: '5x', sublabel: 'Simülasyon' },
    { value: 10, label: '10x', sublabel: 'Simülasyon' },
  ];
  
  // Production API URL
  // Use environment API URL for production stats as well
  const PRODUCTION_API = 'https://borsamex.com'; // Her zaman production'dan veri al
  
  // Fetch live stats from PRODUCTION
  useEffect(() => {
    const fetchLiveStats = async () => {
      try {
        const response = await axios.get(`${PRODUCTION_API}/api/signals/performance`);
        if (response.data.status === 'ok') {
          setLiveStats(response.data);
        }
      } catch (err) {
        console.error('Live stats fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLiveStats();
  }, []);
  
  // Fetch PRODUCTION stats for real performance data (with period filter)
  useEffect(() => {
    const fetchProductionStats = async () => {
      setProductionLoading(true);
      try {
        const response = await axios.get(`${PRODUCTION_API}/api/signals/performance?period=${selectedPeriod}`);
        if (response.data.status === 'ok') {
          setProductionStats(response.data);
        }
      } catch (err) {
        console.error('Production stats fetch error:', err);
      } finally {
        setProductionLoading(false);
      }
    };
    fetchProductionStats();
  }, [selectedPeriod]);  // Re-fetch when period changes
  
  // Fetch ALL TIME stats separately (for stat cards that should not change)
  const [allTimeStats, setAllTimeStats] = useState(null);
  useEffect(() => {
    const fetchAllTimeStats = async () => {
      try {
        const response = await axios.get(`${PRODUCTION_API}/api/signals/performance?period=all`);
        if (response.data.status === 'ok') {
          setAllTimeStats(response.data);
        }
      } catch (err) {
        console.error('All time stats fetch error:', err);
      }
    };
    fetchAllTimeStats();
  }, []);  // Only fetch once on mount
  
  // Fetch equity curve data from PRODUCTION
  useEffect(() => {
    const fetchEquityCurve = async () => {
      setCurveLoading(true);
      try {
        const response = await axios.get(`${PRODUCTION_API}/api/signals/equity-curve?period=${selectedPeriod}`);
        if (response.data.status === 'ok') {
          setEquityCurveData(response.data);
        }
      } catch (err) {
        console.error('Equity curve fetch error:', err);
      } finally {
        setCurveLoading(false);
      }
    };
    fetchEquityCurve();
  }, [selectedPeriod]);
  
  const isPro = user?.subscription_type === 'pro';
  
  // Combined stats: Backtest + Production Live
  const combinedStats = {
    // Backtest data (static)
    backtest_pnl_r: BACKTEST_DATA.totalPnlR, // 129.1R
    backtest_win_rate: BACKTEST_DATA.winRate, // 54%
    backtest_signals: BACKTEST_DATA.totalTrades, // 613
    // Production live data
    live_pnl_r: productionStats?.total_pnl_r || 0, // 32.8R from production
    live_win_rate: productionStats?.win_rate || 0, // 61.2% from production
    live_signals: productionStats?.completed || 0, // 49 completed
    // Combined totals
    total_pnl_r: BACKTEST_DATA.totalPnlR + (productionStats?.total_pnl_r || 0), // 129.1 + 32.8 = 161.9R
    total_pnl_percent: (BACKTEST_DATA.totalPnlR + (productionStats?.total_pnl_r || 0)) * 2, // R to % (2% per R)
    total_signals: BACKTEST_DATA.totalTrades + (productionStats?.completed || 0), // 613 + 49
    // Use production win rate for "live" display (more relevant)
    display_win_rate: productionStats?.win_rate || BACKTEST_DATA.winRate,
  };
  
  // Period options
  const periodOptions = [
    { value: '30d', label: 'Son 30 Gün' },
    { value: '90d', label: 'Son 90 Gün' },
    { value: '6m', label: 'Son 6 Ay' },
    { value: 'all', label: 'Tüm Zamanlar' }
  ];
  
  // Filter equity curve based on selected period
  const getFilteredEquityCurve = () => {
    // Don't calculate until production data is loaded
    if (productionLoading) return null;
    
    const fullCurve = BACKTEST_DATA.equityCurve.map(r => r * 2); // R to % conversion
    // Production sinyallerinden SADECE 2026 ve sonrasını al (backtest ile çakışmayı önle)
    const productionGain = productionStats?.signals 
      ? productionStats.signals
          .filter(s => s.created_at && new Date(s.created_at).getFullYear() >= 2026)
          .reduce((sum, s) => sum + (s.pnl_percent || 0), 0)
      : 0;
    
    // Normalize function - shifts array to start from 0
    const normalize = (arr) => {
      if (arr.length === 0) return arr;
      const startValue = arr[0];
      return arr.map(val => val - startValue);
    };
    
    // Leverage çarpanı uygula
    const applyLeverage = (arr) => arr.map(val => val * leverageMultiplier);
    
    // Aylık kümülatif veriler (her ay için birikimli toplam)
    const monthlyAccumulated = [
      0,                                          // Başlangıç
      MONTHLY_BACKTEST.july,                      // Temmuz sonu: 18.2
      MONTHLY_BACKTEST.july + MONTHLY_BACKTEST.august, // Ağustos sonu: 42.8
      MONTHLY_BACKTEST.july + MONTHLY_BACKTEST.august + MONTHLY_BACKTEST.september, // Eylül sonu: 34.4
      MONTHLY_BACKTEST.july + MONTHLY_BACKTEST.august + MONTHLY_BACKTEST.september + MONTHLY_BACKTEST.october, // Ekim sonu: 65.6
      MONTHLY_BACKTEST.july + MONTHLY_BACKTEST.august + MONTHLY_BACKTEST.september + MONTHLY_BACKTEST.october + MONTHLY_BACKTEST.november, // Kasım sonu: 108.4
      MONTHLY_BACKTEST.july + MONTHLY_BACKTEST.august + MONTHLY_BACKTEST.september + MONTHLY_BACKTEST.october + MONTHLY_BACKTEST.november + MONTHLY_BACKTEST.december, // Aralık sonu: 123.8
    ];
    
    let result;
    switch(selectedPeriod) {
      case '30d':
        // Son 30 gün: Sadece son 30 günün canlı verileri
        if (productionStats?.signals) {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          
          // Son 30 günün sinyallerini tarihe göre grupla
          const dailyPnl = {};
          productionStats.signals
            .filter(s => new Date(s.created_at) >= thirtyDaysAgo && s.pnl_percent)
            .forEach(s => {
              const dateKey = new Date(s.created_at).toDateString();
              dailyPnl[dateKey] = (dailyPnl[dateKey] || 0) + s.pnl_percent;
            });
          
          // Kümülatif grafik oluştur
          const sortedDays = Object.keys(dailyPnl).sort((a, b) => new Date(a) - new Date(b));
          let cumulative = 0;
          const curve = [0];
          sortedDays.forEach(day => {
            cumulative += dailyPnl[day];
            curve.push(parseFloat(cumulative.toFixed(1)));
          });
          result = curve.length > 1 ? curve : [0, productionGain];
        } else {
          result = [0, productionGain];
        }
        break;
      case '90d':
        // Son 90 gün: Kasım + Aralık backtest + Canlı veriler
        const nov90 = [
          0,
          MONTHLY_BACKTEST.november, // Kasım sonu: 42.8
          MONTHLY_BACKTEST.november + MONTHLY_BACKTEST.december, // Aralık sonu: 58.2
        ];
        if (productionGain !== 0) {
          nov90.push(nov90[nov90.length - 1] + productionGain);
        }
        result = nov90;
        break;
      case '6m':
        // Son 6 Ay: Temmuz HARİÇ (Ağustos-Aralık) + canlı veri
        // Temmuz sonundan başla (index 1), normalize et
        const aug6m = [
          0, // Ağustos başı
          MONTHLY_BACKTEST.august, // Ağustos sonu: 24.6
          MONTHLY_BACKTEST.august + MONTHLY_BACKTEST.september, // Eylül sonu: 16.2
          MONTHLY_BACKTEST.august + MONTHLY_BACKTEST.september + MONTHLY_BACKTEST.october, // Ekim sonu: 47.4
          MONTHLY_BACKTEST.august + MONTHLY_BACKTEST.september + MONTHLY_BACKTEST.october + MONTHLY_BACKTEST.november, // Kasım sonu: 90.2
          MONTHLY_BACKTEST.august + MONTHLY_BACKTEST.september + MONTHLY_BACKTEST.october + MONTHLY_BACKTEST.november + MONTHLY_BACKTEST.december, // Aralık sonu: 105.6
        ];
        if (productionGain !== 0) {
          aug6m.push(aug6m[aug6m.length - 1] + productionGain);
        }
        result = aug6m;
        break;
      case 'all':
      default:
        // Tüm Zamanlar: Temmuz'dan başla + canlı
        const allTime = [...monthlyAccumulated];
        if (productionGain !== 0) {
          allTime.push(monthlyAccumulated[monthlyAccumulated.length - 1] + productionGain);
        }
        result = allTime;
        break;
    }
    
    // Leverage çarpanı uygula
    return applyLeverage(result);
  };
  
  const filteredEquityCurve = getFilteredEquityCurve();
  
  // Calculate performance based on selected period
  const getPerformanceForPeriod = () => {
    if (productionLoading) return null;
    
    // Production sinyallerinden SADECE 2026 ve sonrasını al (backtest ile çakışmayı önle)
    const productionPnl = productionStats?.signals 
      ? productionStats.signals
          .filter(s => s.created_at && new Date(s.created_at).getFullYear() >= 2026)
          .reduce((sum, s) => sum + (s.pnl_percent || 0), 0)
      : 0;
    
    // Aylık backtest verileri kullanarak hesapla
    const julyPnl = MONTHLY_BACKTEST.july;           // %18.2
    const augustPnl = MONTHLY_BACKTEST.august;       // %24.6
    const septemberPnl = MONTHLY_BACKTEST.september; // -%8.4
    const octoberPnl = MONTHLY_BACKTEST.october;     // %31.2
    const novemberPnl = MONTHLY_BACKTEST.november;   // %42.8
    const decemberPnl = MONTHLY_BACKTEST.december;   // %15.4
    
    // Tüm backtest toplamı
    const backtestTotal = julyPnl + augustPnl + septemberPnl + octoberPnl + novemberPnl + decemberPnl;
    
    let baseResult;
    switch(selectedPeriod) {
      case '30d':
        // Son 30 gün: Sadece son 30 günün canlı verileri
        // Bugünden 30 gün öncesinden itibaren olan sinyallerin PnL'i
        if (productionStats?.signals) {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          
          const last30DaysPnl = productionStats.signals
            .filter(s => new Date(s.created_at) >= thirtyDaysAgo && s.pnl_percent)
            .reduce((sum, s) => sum + s.pnl_percent, 0);
          
          baseResult = last30DaysPnl;
        } else {
          baseResult = 0;
        }
        break;
      case '90d':
        // Son 90 gün: Kasım backtest + Aralık backtest + Canlı veriler (sadece 2026+)
        baseResult = novemberPnl + decemberPnl + productionPnl;
        break;
      case '6m':
        // Son 6 Ay: Temmuz HARİÇ (Ağustos-Aralık) + canlı veri (sadece 2026+)
        const sixMonthBacktest = augustPnl + septemberPnl + octoberPnl + novemberPnl + decemberPnl;
        baseResult = sixMonthBacktest + productionPnl;
        break;
      case 'all':
      default:
        // Tüm Zamanlar: Tüm backtest + Canlı production (sadece 2026+)
        baseResult = backtestTotal + productionPnl;
        break;
    }
    
    // Leverage çarpanı uygula
    return Math.round(baseResult * leverageMultiplier);
  };
  
  const periodPerformance = getPerformanceForPeriod();
  
  // Get period label for stat card
  const getPeriodLabel = () => {
    switch(selectedPeriod) {
      case '30d': return 'Son 30 Gün';
      case '90d': return 'Son 90 Gün';
      case '6m': return 'Son 6 Ay';
      default: return 'Tüm Zamanlar';
    }
  };

  // FAQ Data for Schema
  const faqData = [
    {
      question: "Performans verileri nasıl hesaplanır?",
      answer: "Performans ölçümleri, sinyallerin belirlenen hedef (TP) ve stop seviyelerine göre gerçekleşen sonuçlara dayanır. Tüm veriler geçmiş sinyal davranışlarını yansıtır."
    },
    {
      question: "Negatif aylar ne anlama gelir?",
      answer: "Kripto piyasalarının doğal dalgalı yapısı nedeniyle bazı dönemlerde gerilemeler oluşabilir. Bu durum sistemin normal işleyişinin bir parçasıdır."
    },
    {
      question: "Geçmiş performans garanti midir?",
      answer: "Hayır. Geçmiş veriler yalnızca sistem davranışını gösterir, gelecek sonuçları garanti etmez. Tüm yatırım kararları kullanıcıya aittir."
    },
    {
      question: "Maksimum gerileme nedir?",
      answer: "Belirli bir dönemde görülen en yüksek düşüş oranını ifade eder. Bu metrik risk yönetimi açısından önemli bir göstergedir."
    },
    {
      question: "Risk & Getiri Simülatörü neyi gösterir?",
      answer: "Farklı sermaye ve kaldıraç senaryolarında sistemin geçmiş performansına dayalı örnek hesaplamalardır. Eğitim amaçlıdır."
    }
  ];

  // Structured Data for SEO
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Kripto Sinyal Performansı – Geçmiş Sonuçlar & Sistem İstatistikleri",
      "description": "Borsamex algoritmalarının ürettiği sinyallerin geçmiş verileri ve sistem ölçümleri.",
      "url": "https://borsamex.com/performans"
    },
    {
      "@context": "https://schema.org",
      "@type": "Dataset",
      "name": "Borsamex Kripto Sinyal Performans Verileri",
      "description": "AI destekli kripto sinyal sisteminin geçmiş performans istatistikleri ve ölçümleri.",
      "url": "https://borsamex.com/performans",
      "creator": {
        "@type": "Organization",
        "name": "Borsamex"
      },
      "temporalCoverage": "2025-07/2026-02",
      "variableMeasured": [
        {
          "@type": "PropertyValue",
          "name": "Toplam Getiri",
          "description": "Sistemin toplam kümülatif getiri oranı"
        },
        {
          "@type": "PropertyValue",
          "name": "Başarı Oranı",
          "description": "Hedefine ulaşan sinyallerin yüzdesi"
        },
        {
          "@type": "PropertyValue",
          "name": "Maksimum Gerileme",
          "description": "Dönem içinde görülen en yüksek düşüş oranı"
        },
        {
          "@type": "PropertyValue",
          "name": "Toplam Sinyal Sayısı",
          "description": "Üretilen toplam sinyal adedi"
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": { "@type": "Answer", "text": item.answer }
      }))
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO 
        title="Kripto Sinyal Performansı – Geçmiş Sonuçlar & Sistem İstatistikleri | Borsamex"
        description="Borsamex algoritmalarının ürettiği sinyallerin geçmiş verileri ve sistem ölçümleri. Şeffaf performans istatistikleri."
        keywords="kripto sinyal performansı, trading sinyal sonuçları, sinyal başarı oranı, kripto bot performansı, backtest sonuçları"
        canonical="https://borsamex.com/performans"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-purple-500/10" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-900/20 via-transparent to-transparent" />
        
        <div className="relative max-w-6xl mx-auto px-4 py-12 md:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Kripto Sinyal <span className="text-emerald-400">Performansı</span>
            </h1>
            <p className="text-zinc-400 max-w-2xl mx-auto mb-6">
              Borsamex algoritmalarının ürettiği sinyallerin geçmiş verileri ve sistem ölçümleri
            </p>
            
            {/* CTA Button - Visible for visitors */}
            {!user && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex justify-center gap-3"
              >
                <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8">
                  <Link to="/register">
                    Ücretsiz Başla <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </motion.div>
            )}
          </motion.div>
          
          {/* Main Stats Grid - SPK Uyumlu: Ana KPI'lar (Seçilen döneme göre) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <StatCard
              icon={TrendingUp}
              label="Dönem Performansı"
              value={productionLoading ? (
                <span className="animate-pulse bg-zinc-700 rounded w-20 h-8 inline-block" />
              ) : `+%${periodPerformance}`}
              subValue={getPeriodLabel()}
              color="emerald"
              delay={0.1}
            />
            <StatCard
              icon={BarChart3}
              label="Toplam Sinyal"
              value={!allTimeStats ? (
                <span className="animate-pulse bg-zinc-700 rounded w-16 h-8 inline-block" />
              ) : `${BACKTEST_DATA.totalTrades + (allTimeStats?.completed || 0)}`}
              subValue="Tüm Zamanlar"
              color="blue"
              delay={0.15}
            />
            <StatCard
              icon={TrendingUp}
              label="Profit Factor"
              value={!allTimeStats ? (
                <span className="animate-pulse bg-zinc-700 rounded w-16 h-8 inline-block" />
              ) : (() => {
                // ═══════════════════════════════════════════════════════════
                // DOĞRU HESAPLAMA: Backtest + Canlı birleşik Profit Factor
                // PF = (Backtest GP + Canlı GP) / (Backtest GL + Canlı GL)
                // ═══════════════════════════════════════════════════════════
                
                // Backtest verileri (Temmuz 2025 - Ocak 2026)
                // 662 trade, PF: 1.46, ~361 win @ avg 1.8%, ~301 loss @ avg 1.23%
                const backtestGrossProfit = 361 * 1.8;  // ~649.8
                const backtestGrossLoss = 301 * 1.23;   // ~370.23
                
                // Canlı veriler (API'den)
                const liveGrossProfit = allTimeStats?.gross_profit || 0;
                const liveGrossLoss = allTimeStats?.gross_loss || 0;
                
                // Birleşik hesaplama
                const totalGrossProfit = backtestGrossProfit + liveGrossProfit;
                const totalGrossLoss = backtestGrossLoss + liveGrossLoss;
                
                let combinedPF;
                if (totalGrossLoss > 0) {
                  combinedPF = totalGrossProfit / totalGrossLoss;
                } else if (totalGrossProfit > 0) {
                  combinedPF = 99.0;
                } else {
                  combinedPF = 0;
                }
                
                return `1 : ${combinedPF.toFixed(2)}`;
              })()}
              subValue="Tüm zamanlar"
              color="purple"
              delay={0.2}
            />
            <StatCard
              icon={Shield}
              label="Maksimum Gerileme"
              value={(() => {
                // Dönem bazlı backtest drawdown değerleri
                // NOT: Canlı verilerde hatalı kayıt var (UNI-USDT -20.4%), 
                // bu yüzden şimdilik backtest değerlerini kullanıyoruz
                let baseDrawdown;
                switch(selectedPeriod) {
                  case '30d':
                    baseDrawdown = 4.2; // Son 30 gün tahmini
                    break;
                  case '90d':
                    baseDrawdown = 5.8; // Kas+Ara
                    break;
                  case '6m':
                    baseDrawdown = 8.4; // Eylül düşüşü dahil
                    break;
                  case 'all':
                  default:
                    baseDrawdown = BACKTEST_DATA.maxDrawdown; // 9.5%
                    break;
                }
                
                const leveragedDrawdown = baseDrawdown * leverageMultiplier;
                const cappedDrawdown = Math.min(leveragedDrawdown, 100);
                
                return `-%${cappedDrawdown.toFixed(1)}`;
              })()}
              subValue={(() => {
                let periodLabel;
                switch(selectedPeriod) {
                  case '30d': periodLabel = 'Son 30 gün'; break;
                  case '90d': periodLabel = 'Son 90 gün'; break;
                  case '6m': periodLabel = 'Son 6 ay'; break;
                  default: periodLabel = 'Tüm zamanlar'; break;
                }
                return leverageMultiplier > 1 
                  ? `${periodLabel} • ${leverageMultiplier}x sim.` 
                  : periodLabel;
              })()}
              color="amber"
              delay={0.25}
            />
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        <div className="grid md:grid-cols-3 gap-6">
          
          {/* Left Column - Equity Curve & Live Stats */}
          <div className="md:col-span-2 space-y-6 flex flex-col">
            
            {/* Sinyallerin Birikimli Sonucu - Tek Equity Curve */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                    <div>
                      <h3 className="font-semibold text-white flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-emerald-400" />
                        Sinyallerin Birikimli Sonucu
                      </h3>
                      <p className="text-xs text-zinc-500 mt-1">
                        {periodOptions.find(p => p.value === selectedPeriod)?.label || 'Tüm Zamanlar'}
                      </p>
                    </div>
                    
                    {/* Period Toggle */}
                    <div className="flex flex-wrap gap-1.5">
                      {periodOptions.map(option => (
                        <button
                          key={option.value}
                          onClick={() => setSelectedPeriod(option.value)}
                          className={`px-3 py-1.5 text-xs rounded-lg transition-all ${
                            selectedPeriod === option.value
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                              : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-700/50 border border-transparent'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Leverage / Risk Senaryo Simülasyonu */}
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex items-center gap-1.5 relative group">
                      <span className="text-xs text-zinc-400">Risk Senaryosu</span>
                      {/* Info Icon with Tooltip */}
                      <div className="relative">
                        <span className="text-zinc-500 hover:text-zinc-300 cursor-help text-xs">ⓘ</span>
                        {/* Tooltip */}
                        <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                          <p className="text-[11px] text-zinc-300 leading-relaxed">
                            <strong className="text-zinc-100">Kaldıraçlı işlemler yüksek risk içerir.</strong>
                            <br /><br />
                            Gösterilen değerler teorik simülasyondur, gerçek piyasa sonuçlarını temsil etmez. 
                            Likidasyon riski ve farklı sonuçlar oluşabilir.
                          </p>
                          {/* Tooltip arrow */}
                          <div className="absolute left-3 bottom-[-6px] w-3 h-3 bg-zinc-900 border-r border-b border-zinc-700 transform rotate-45"></div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {leverageOptions.map(option => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setLeverageMultiplier(option.value);
                            if (option.value > 1) setShowLeverageWarning(true);
                          }}
                          className={`px-2.5 py-1 text-xs rounded-md transition-all ${
                            leverageMultiplier === option.value
                              ? option.value === 1 
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                                : 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                              : 'bg-zinc-700/50 text-zinc-400 hover:bg-zinc-600/50 border border-transparent'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Equity Curve - Period Filtered with BTC Benchmark (only on "all") */}
                  {productionLoading || !filteredEquityCurve ? (
                    <div className="h-[180px] flex items-center justify-center">
                      <div className="animate-pulse flex flex-col items-center gap-2">
                        <div className="w-full h-32 bg-zinc-800 rounded" />
                        <span className="text-xs text-zinc-500">Grafik yükleniyor...</span>
                      </div>
                    </div>
                  ) : (
                    <EquityCurveChart data={filteredEquityCurve} height={180} showBenchmark={false} />
                  )}
                  
                  {/* Date Range Label - Dynamic based on period */}
                  <div className="text-center mt-3 pt-3 border-t border-zinc-800">
                    <span className="text-xs text-zinc-500">
                      📅 Veri Aralığı: {(() => {
                        const today = new Date();
                        const endDate = `${today.getDate().toString().padStart(2, '0')}.${(today.getMonth() + 1).toString().padStart(2, '0')}.${today.getFullYear()}`;
                        
                        switch(selectedPeriod) {
                          case '30d': {
                            const start = new Date();
                            start.setDate(start.getDate() - 30);
                            const startDate = `${start.getDate().toString().padStart(2, '0')}.${(start.getMonth() + 1).toString().padStart(2, '0')}.${start.getFullYear()}`;
                            return `${startDate} – ${endDate}`;
                          }
                          case '90d': {
                            const start = new Date();
                            start.setDate(start.getDate() - 90);
                            const startDate = `${start.getDate().toString().padStart(2, '0')}.${(start.getMonth() + 1).toString().padStart(2, '0')}.${start.getFullYear()}`;
                            return `${startDate} – ${endDate}`;
                          }
                          case '6m':
                            return `01.08.2025 – ${endDate}`;
                          case 'all':
                          default:
                            return `01.07.2025 – ${endDate}`;
                        }
                      })()}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            
            {/* Monthly Returns Heatmap - Backtest + Live */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-400" />
                    Aylık Performans
                  </h3>
                  {(() => {
                    // Backtest verileri (Tem-Ara 2025) - Sabit
                    // Toplam 613 sinyal, aylık dağılım tahmini
                    const backtestMonths = [
                      { month: 'Tem', year: 2025, value: 18.2, isBacktest: true, signalCount: 98 },
                      { month: 'Ağu', year: 2025, value: 24.6, isBacktest: true, signalCount: 105 },
                      { month: 'Eyl', year: 2025, value: -8.4, isBacktest: true, signalCount: 95 },
                      { month: 'Eki', year: 2025, value: 31.2, isBacktest: true, signalCount: 108 },
                      { month: 'Kas', year: 2025, value: 42.8, isBacktest: true, signalCount: 112 },
                      { month: 'Ara', year: 2025, value: 15.4, isBacktest: true, signalCount: 95 },
                    ];
                    
                    // Canlı verilerden aylık performans hesapla (Ocak 2026+)
                    // NOT: allTimeStats kullanılıyor çünkü aylık kartlar seçilen dönemden bağımsız olmalı
                    const liveMonths = [];
                    const monthNames = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
                    
                    // allTimeStats'ı kullan (her zaman tüm verileri içerir)
                    const statsToUse = allTimeStats || productionStats;
                    
                    if (statsToUse?.signals && !productionLoading) {
                      // Sinyalleri OLUŞTURULDUĞU AYA göre grupla
                      // pnl_percent değeri zaten sinyal kapandığında hesaplanmış ve sabitlenmiş olmalı
                      const monthlyPnl = {};
                      
                      statsToUse.signals.forEach(signal => {
                        if (!signal.created_at || signal.pnl_percent === undefined || signal.pnl_percent === null) return;
                        
                        const date = new Date(signal.created_at);
                        const year = date.getFullYear();
                        const month = date.getMonth(); // 0-11
                        
                        // Sadece 2026 ve sonrası (canlı dönem)
                        if (year < 2026) return;
                        
                        const key = `${year}-${month}`;
                        if (!monthlyPnl[key]) {
                          monthlyPnl[key] = { total: 0, count: 0, year, month };
                        }
                        monthlyPnl[key].total += signal.pnl_percent;
                        monthlyPnl[key].count += 1;
                      });
                      
                      // Aylık verileri sırala ve ekle
                      Object.values(monthlyPnl)
                        .sort((a, b) => a.year - b.year || a.month - b.month)
                        .forEach(data => {
                          liveMonths.push({
                            month: monthNames[data.month],
                            year: data.year,
                            value: parseFloat(data.total.toFixed(1)),
                            isBacktest: false,
                            signalCount: data.count
                          });
                        });
                    }
                    
                    // Tüm ayları birleştir
                    const allMonths = [...backtestMonths, ...liveMonths];
                    
                    // İstatistikler
                    const positiveMonths = allMonths.filter(m => m.value >= 0).length;
                    const avgMonthly = allMonths.reduce((sum, m) => sum + m.value, 0) / allMonths.length;
                    
                    // Ay kartı render fonksiyonu - leverage çarpanı uygulanmış
                    const renderMonthCard = (item, idx) => {
                      const displayValue = item.value * leverageMultiplier;
                      // Büyük değerler için kısaltma (1000+ -> 1.2k)
                      const formatValue = (val) => {
                        if (Math.abs(val) >= 1000) {
                          return (val / 1000).toFixed(1) + 'k';
                        }
                        return val.toFixed(1);
                      };
                      
                      return (
                        <div 
                          key={idx}
                          className={`text-center p-1.5 sm:p-2 rounded-lg ${
                            displayValue >= 0 
                              ? 'bg-emerald-500/10 border border-emerald-500/20' 
                              : 'bg-red-500/10 border border-red-500/20'
                          }`}
                        >
                          <div className="text-[9px] sm:text-[10px] text-zinc-500 mb-0.5 sm:mb-1">
                            {item.month}
                          </div>
                          <div className={`text-[11px] sm:text-sm font-bold whitespace-nowrap ${
                            displayValue >= 0 ? 'text-emerald-400' : 'text-red-400'
                          }`}>
                            {displayValue >= 0 ? '+' : ''}{formatValue(displayValue)}%
                          </div>
                        </div>
                      );
                    };
                    
                    return (
                      <>
                        {/* 2025 Satırı */}
                        <div className="mb-2">
                          <div className="text-[10px] text-zinc-500 mb-1.5 font-medium">2025</div>
                          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 sm:gap-2">
                            {backtestMonths.map((item, idx) => renderMonthCard(item, idx))}
                          </div>
                        </div>
                        
                        {/* 2026 Satırı */}
                        {liveMonths.length > 0 && (
                          <div className="mt-3">
                            <div className="text-[10px] text-zinc-500 mb-1.5 font-medium">2026</div>
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 sm:gap-2">
                              {liveMonths.map((item, idx) => renderMonthCard(item, `live-${idx}`))}
                            </div>
                          </div>
                        )}
                        
                        <p className="text-[9px] sm:text-[10px] text-zinc-600 mt-3 text-center">
                          {allMonths.length} ayın {positiveMonths}'i pozitif kapanış • Ortalama aylık: {(avgMonthly * leverageMultiplier) >= 0 ? '+' : ''}{(avgMonthly * leverageMultiplier).toFixed(1)}%
                          {leverageMultiplier > 1 && <span className="text-amber-400/60 ml-1">({leverageMultiplier}x sim.)</span>}
                        </p>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </motion.div>
            
            {/* How It Works - Mini Section - Flex grow to fill space */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex-1"
            >
              <Card className="bg-zinc-900/50 border-zinc-800 h-full">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-400" />
                    Sistem Nasıl Çalışıyor?
                  </h3>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-emerald-400 font-bold text-sm">1</span>
                      </div>
                      <div>
                        <div className="text-white font-medium text-sm">Trend Analizi</div>
                        <div className="text-zinc-500 text-xs">Teknik göstergelerle piyasa yönünü analiz eder</div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-400 font-bold text-sm">2</span>
                      </div>
                      <div>
                        <div className="text-white font-medium text-sm">Giriş Seviyesi</div>
                        <div className="text-zinc-500 text-xs">Destek seviyelerinde potansiyel giriş noktaları belirler</div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-purple-400 font-bold text-sm">3</span>
                      </div>
                      <div>
                        <div className="text-white font-medium text-sm">Risk Yönetimi</div>
                        <div className="text-zinc-500 text-xs">Otomatik stop-loss ve kâr alma seviyeleri hesaplar</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
          
          {/* Right Column - Coins & Calculator */}
          <div className="space-y-6 flex flex-col">
            
            {/* TOP 5 Coins - Backtest + Live Combined */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <Award className="w-4 h-4 text-amber-400" />
                      Top 5 Coin
                    </h3>
                    <Badge variant="outline" className="text-zinc-400 text-xs">
                      {periodOptions.find(p => p.value === selectedPeriod)?.label || 'Tüm Zamanlar'}
                    </Badge>
                  </div>
                  <p className="text-xs text-zinc-500 mb-4">En yüksek performans gösteren coinler</p>
                  <div className="space-y-0">
                    {productionLoading ? (
                      // Loading skeleton
                      [...Array(5)].map((_, i) => (
                        <div key={i} className="flex items-center justify-between py-3 border-b border-zinc-800/50 last:border-0">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-zinc-800 animate-pulse" />
                            <div>
                              <div className="w-12 h-4 bg-zinc-800 rounded animate-pulse mb-1" />
                              <div className="w-20 h-3 bg-zinc-800 rounded animate-pulse" />
                            </div>
                          </div>
                          <div className="w-16 h-6 bg-zinc-800 rounded animate-pulse" />
                        </div>
                      ))
                    ) : (
                      // PERIOD-BASED Top 5 Coins
                      (() => {
                        // Dönem bazlı tarih hesapla
                        const now = new Date();
                        const thirtyDaysAgo = new Date(now);
                        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                        const ninetyDaysAgo = new Date(now);
                        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
                        
                        // Coin renkleri
                        const coinColors = {
                          'ETH': '#627EEA', 'APT': '#00D4AA', 'AAVE': '#B6509E', 'ADA': '#0033AD',
                          'NEAR': '#00C08B', 'LINK': '#2A5ADA', 'SOL': '#9945FF', 'DOGE': '#C2A633',
                          'FET': '#1D2951', 'UNI': '#FF007A', 'BTC': '#F7931A', 'AVAX': '#E84142',
                          'ATOM': '#2E3148', 'DOT': '#E6007A', 'LTC': '#BFBBBB', 'TRX': '#FF0013',
                          'XRP': '#23292F', 'MATIC': '#8247E5'
                        };
                        
                        // Dönem bazlı backtest çarpanı
                        let backtestMultiplier = 1;
                        switch(selectedPeriod) {
                          case '30d': backtestMultiplier = 0; break;
                          case '90d': backtestMultiplier = 0.47; break; // Kas+Ara
                          case '6m': backtestMultiplier = 0.85; break; // Ağu-Ara
                          default: backtestMultiplier = 1; break;
                        }
                        
                        // Canlı verileri coin bazında topla - DÖNEM FİLTRELİ
                        const liveBySymbol = {};
                        if (productionStats?.signals) {
                          productionStats.signals.forEach(signal => {
                            const signalDate = new Date(signal.created_at);
                            
                            // Dönem filtrelemesi
                            if (selectedPeriod === '30d' && signalDate < thirtyDaysAgo) return;
                            if (selectedPeriod === '90d' && signalDate < ninetyDaysAgo) return;
                            
                            const sym = signal.symbol?.replace('-USDT', '').replace('-USD', '') || '';
                            if (!sym) return;
                            
                            if (!liveBySymbol[sym]) {
                              liveBySymbol[sym] = { pnl: 0, trades: 0, wins: 0 };
                            }
                            liveBySymbol[sym].trades += 1;
                            if (signal.pnl_percent) {
                              liveBySymbol[sym].pnl += signal.pnl_percent;
                            }
                            if (signal.tp1_hit || signal.tp2_hit || signal.tp3_hit) {
                              liveBySymbol[sym].wins += 1;
                            }
                          });
                        }
                        
                        let allCoins = [];
                        
                        if (selectedPeriod === '30d') {
                          // Son 30 gün: SADECE canlı verilerden coin listesi oluştur
                          allCoins = Object.entries(liveBySymbol).map(([symbol, data]) => ({
                            symbol,
                            color: coinColors[symbol] || '#6B7280',
                            trades: data.trades,
                            winRate: data.trades > 0 ? (data.wins / data.trades) * 100 : 0,
                            pnlDollar: data.pnl * 100 // CoinRow formatı için
                          }));
                        } else {
                          // Diğer dönemler: Backtest + canlı veri birleştir
                          // Önce backtest coinlerini ekle
                          const coinMap = {};
                          
                          BACKTEST_DATA.coins.forEach(coin => {
                            const scaledTrades = Math.round(coin.trades * backtestMultiplier);
                            const scaledPnl = (coin.pnlDollar / 100) * backtestMultiplier;
                            const scaledWins = Math.round(coin.trades * coin.winRate / 100 * backtestMultiplier);
                            
                            coinMap[coin.symbol] = {
                              symbol: coin.symbol,
                              color: coin.color,
                              trades: scaledTrades,
                              wins: scaledWins,
                              pnl: scaledPnl
                            };
                          });
                          
                          // Canlı verileri ekle/birleştir
                          Object.entries(liveBySymbol).forEach(([symbol, data]) => {
                            if (coinMap[symbol]) {
                              coinMap[symbol].trades += data.trades;
                              coinMap[symbol].wins += data.wins;
                              coinMap[symbol].pnl += data.pnl;
                            } else {
                              coinMap[symbol] = {
                                symbol,
                                color: coinColors[symbol] || '#6B7280',
                                trades: data.trades,
                                wins: data.wins,
                                pnl: data.pnl
                              };
                            }
                          });
                          
                          allCoins = Object.values(coinMap).map(coin => ({
                            ...coin,
                            winRate: coin.trades > 0 ? (coin.wins / coin.trades) * 100 : 0,
                            pnlDollar: coin.pnl * 100
                          }));
                        }
                        
                        // PnL'e göre sırala ve Top 5 al
                        const top5 = allCoins
                          .filter(c => c.trades > 0) // Sadece işlem yapılanlar
                          .sort((a, b) => b.pnlDollar - a.pnlDollar)
                          .slice(0, 5);
                        
                        // Veri yoksa mesaj göster
                        if (top5.length === 0) {
                          return (
                            <div className="text-center py-6 text-zinc-500 text-sm">
                              Bu dönemde yeterli veri yok
                            </div>
                          );
                        }
                        
                        return top5.map((coin, index) => (
                          <CoinRow key={coin.symbol} coin={coin} index={index} leverageMultiplier={leverageMultiplier} />
                        ));
                      })()
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            
            {/* Risk & Return Simulator - flex-1 to match left column */}
            <div className="flex-1">
              <RiskReturnSimulator productionStats={productionStats} productionLoading={productionLoading} allTimeStats={allTimeStats} />
            </div>
          </div>
        </div>
      </section>

      {/* SEO Text Layer - Semantic Content for Google */}
      <section className="py-10 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-zinc-500 text-sm leading-relaxed max-w-2xl mx-auto">
              Sistem performansı piyasa koşullarına göre dönemsel değişim gösterebilir. 
              Kripto varlıkların volatil yapısı nedeniyle bazı dönemlerde gerilemeler görülebilirken, 
              trend oluşan piyasalarda performans farklılaşabilir.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-zinc-900/50 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl font-bold text-white mb-3">Sık Sorulan Sorular</h2>
            <p className="text-zinc-400 text-sm">Performans verileri hakkında merak edilenler</p>
          </motion.div>

          <div className="space-y-3">
            {faqData.map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                viewport={{ once: true }}
              >
                <details className="group">
                  <summary className="flex items-center justify-between p-4 bg-zinc-800/50 border border-zinc-700/50 rounded-lg cursor-pointer hover:bg-zinc-800 transition-colors">
                    <span className="font-medium text-white pr-4">{faq.question}</span>
                    <ChevronRight className="h-5 w-5 text-zinc-400 group-open:rotate-90 transition-transform shrink-0" />
                  </summary>
                  <div className="px-4 py-3 text-sm text-zinc-400 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rehber Links */}
      <section className="py-10 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-6">
            <h3 className="text-lg font-semibold text-white mb-2">Temel Kavramları Öğren</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <Link to="/rehber/kripto-sinyali-nedir" className="group">
              <Card className="border-purple-500/20 bg-zinc-900/50 h-full hover:border-purple-500/50 transition-colors">
                <CardContent className="p-4">
                  <h4 className="font-medium text-white mb-1 group-hover:text-purple-400 transition-colors">Kripto Sinyali Nedir?</h4>
                  <p className="text-zinc-500 text-xs">Temel kavramları öğren</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/rehber/tp-ve-stop-loss-nedir" className="group">
              <Card className="border-green-500/20 bg-zinc-900/50 h-full hover:border-green-500/50 transition-colors">
                <CardContent className="p-4">
                  <h4 className="font-medium text-white mb-1 group-hover:text-green-400 transition-colors">TP ve Stop Loss Nedir?</h4>
                  <p className="text-zinc-500 text-xs">Hedef ve risk seviyeleri</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/rehber/yeni-baslayanlar-icin-risk-yonetimi" className="group">
              <Card className="border-red-500/20 bg-zinc-900/50 h-full hover:border-red-500/50 transition-colors">
                <CardContent className="p-4">
                  <h4 className="font-medium text-white mb-1 group-hover:text-red-400 transition-colors">Risk Yönetimi</h4>
                  <p className="text-zinc-500 text-xs">Sermaye koruma stratejileri</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Compact Professional CTA */}
      <section className="py-8 bg-zinc-900 border-t border-zinc-800">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-gradient-to-r from-emerald-600/10 to-teal-600/10 rounded-xl p-6 border border-emerald-500/20">
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">
                {user ? 'Sinyalleri incelemeye hazır mısın?' : 'Sistemi tanımaya hazır mısın?'}
              </h3>
              <p className="text-sm text-zinc-400">
                {user ? 'Aktif sinyalleri ve pozisyonları incele.' : 'Ücretsiz hesap oluştur, sinyalleri incele, sonra karar ver.'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button asChild variant="outline" size="sm" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                <Link to="/nasil-calisir">
                  Nasıl Çalışır?
                </Link>
              </Button>
              <Button asChild size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Link to={user ? "/signals" : "/register"}>
                  {user ? 'Sinyallere Git' : 'Ücretsiz Başla'} <ArrowRight className="w-3 h-3 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PerformanceDashboard;
